import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import RhythmSection from "@/components/RhythmSection";
import { LoadingSpinner } from "@/components/ui/spinner";
import Footer from "@/components/Footer";

const EquilibriumSection = lazy(() => import("@/components/EquilibriumSection"));
const MarketsSection = lazy(() => import("@/components/MarketsSection"));
const PlatformSection = lazy(() => import("@/components/PlatformSection"));

type DeferredSectionProps = {
  children: ReactNode;
  minHeight: number;
  rootMargin?: string;
};

const isCrawlerUserAgent = () => {
  if (typeof navigator === "undefined") return false;
  return /bot|crawler|spider|crawling|google|bing|yandex|duckduck|baidu|lighthouse|pagespeed/i.test(navigator.userAgent);
};

const DeferredSection = ({ children, minHeight, rootMargin = "300px 0px" }: DeferredSectionProps) => {
  const [shouldRender, setShouldRender] = useState(() => isCrawlerUserAgent());
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (shouldRender) return;

    const timeoutId = window.setTimeout(() => {
      setShouldRender(true);
    }, 2500);

    if (!("IntersectionObserver" in window)) {
      setShouldRender(true);
      return () => window.clearTimeout(timeoutId);
    }

    const node = ref.current;

    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldRender(true);
        observer.disconnect();
      },
      { rootMargin },
    );

    observer.observe(node);

    return () => {
      window.clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [rootMargin, shouldRender]);

  return (
    <div ref={ref}>
      {shouldRender ? children : <div style={{ minHeight }} aria-hidden="true" />}
    </div>
  );
};

const Index = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      <HeroSection />
      <RhythmSection />

      <DeferredSection minHeight={400}>
        <Suspense fallback={
          <div className="min-h-[400px] flex items-center justify-center">
            <LoadingSpinner />
          </div>
        }>
          <EquilibriumSection />
        </Suspense>
      </DeferredSection>

      <DeferredSection minHeight={480}>
        <Suspense fallback={
          <div className="min-h-[400px] flex items-center justify-center">
            <LoadingSpinner />
          </div>
        }>
          <MarketsSection />
        </Suspense>
      </DeferredSection>

      <DeferredSection minHeight={480}>
        <Suspense fallback={
          <div className="min-h-[400px] flex items-center justify-center">
            <LoadingSpinner />
          </div>
        }>
          <PlatformSection />
        </Suspense>
      </DeferredSection>

      <Footer />
    </div>
  );
};

export default Index;
